import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { getAllProducts } from "../../firebase/productService";
import { getAllOrders } from "../../firebase/orderService";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageTitle = styled.h1`
  color: #fff;
  font-size: 2rem;
  font-weight: 400;
  letter-spacing: 0.05em;
  margin: 2em 0 4px 0;
`;

const PageSub = styled.p`
  color: #444;
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin: 0 0 48px 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 48px;
`;

const StatCard = styled.div`
  background: #111;
  border: 1px solid #1e1e1e;
  padding: 28px 24px;
  animation: ${fadeIn} 0.4s ease forwards;
  animation-delay: ${(p) => p.$delay || "0s"};
  opacity: 0;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  transition: border-color 0.2s;

  &:hover {
    border-color: ${(p) => (p.$clickable ? "#333" : "#1e1e1e")};
  }
`;

const StatValue = styled.div`
  color: #fff;
  font-size: 2.4rem;
  font-weight: 400;
  letter-spacing: -0.02em;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  color: #444;
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 0.8rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 400;
  margin: 0 0 20px 0;
`;

const OrderTable = styled.div`
  border: 1px solid #1e1e1e;
  overflow: hidden;
`;

const OrderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr 100px 100px;
  border-bottom: 1px solid #1e1e1e;
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #161616;
  }
`;

const OrderHeaderRow = styled(OrderRow)`
  background: #0a0a0a;
  &:hover {
    background: #0a0a0a;
  }
`;

const OrderCell = styled.div`
  padding: 12px 16px;
  color: ${(p) => (p.$header ? "#444" : "#aaa")};
  font-size: ${(p) => (p.$header ? "0.62rem" : "0.8rem")};
  letter-spacing: ${(p) => (p.$header ? "0.15em" : "0.03em")};
  text-transform: ${(p) => (p.$header ? "uppercase" : "none")};
  display: flex;
  align-items: center;
`;

const StatusBadge = styled.span`
  font-size: 0.6rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 3px 8px;
  border: 1px solid
    ${(p) => {
      if (p.$status === "paid") return "#2ecc71";
      if (p.$status === "pending") return "#f39c12";
      return "#555";
    }};
  color: ${(p) => {
    if (p.$status === "paid") return "#2ecc71";
    if (p.$status === "pending") return "#f39c12";
    return "#555";
  }};
`;

const EmptyState = styled.div`
  color: #333;
  font-size: 0.8rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-align: center;
  padding: 48px;
  border: 1px dashed #1e1e1e;
`;

const AdminDashboard = () => {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [p, o] = await Promise.all([getAllProducts(), getAllOrders()]);
        setProducts(p);
        setOrders(o);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const activeProducts = products.filter((p) => p.active).length;
  const recentOrders = orders.slice(0, 5);

  const formatDate = (ts) => {
    if (!ts) return "—";
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <AdminLayout>
      <PageTitle>Dashboard</PageTitle>
      <PageSub>Overview of your studio</PageSub>

      <StatsGrid>
        <StatCard
          $delay="0s"
          $clickable
          onClick={() => navigate("/admin/products")}
        >
          <StatValue>{loading ? "—" : products.length}</StatValue>
          <StatLabel>Total Products</StatLabel>
        </StatCard>
        <StatCard
          $delay="0.1s"
          $clickable
          onClick={() => navigate("/admin/products")}
        >
          <StatValue>{loading ? "—" : activeProducts}</StatValue>
          <StatLabel>Live Products</StatLabel>
        </StatCard>
        <StatCard
          $delay="0.2s"
          $clickable
          onClick={() => navigate("/admin/orders")}
        >
          <StatValue>{loading ? "—" : orders.length}</StatValue>
          <StatLabel>Total Orders</StatLabel>
        </StatCard>
        <StatCard $delay="0.3s">
          <StatValue>
            {loading
              ? "—"
              : orders.filter((o) => o.status === "pending").length}
          </StatValue>
          <StatLabel>Pending Orders</StatLabel>
        </StatCard>
      </StatsGrid>

      <SectionTitle>Recent Orders</SectionTitle>
      {loading ? (
        <EmptyState>Loading...</EmptyState>
      ) : recentOrders.length === 0 ? (
        <EmptyState>No orders yet.</EmptyState>
      ) : (
        <OrderTable>
          <OrderHeaderRow>
            <OrderCell $header>Order ID</OrderCell>
            <OrderCell $header>Product</OrderCell>
            <OrderCell $header>Date</OrderCell>
            <OrderCell $header>Price</OrderCell>
            <OrderCell $header>Status</OrderCell>
          </OrderHeaderRow>
          {recentOrders.map((o) => (
            <OrderRow key={o.id}>
              <OrderCell
                style={{
                  fontFamily: "monospace",
                  fontSize: "0.7rem",
                  color: "#555",
                }}
              >
                {o.id.slice(0, 8)}...
              </OrderCell>
              <OrderCell>{o.product?.name || "—"}</OrderCell>
              <OrderCell>{formatDate(o.createdAt)}</OrderCell>
              <OrderCell>€{o.product?.price || "—"}</OrderCell>
              <OrderCell>
                <StatusBadge $status={o.status}>{o.status}</StatusBadge>
              </OrderCell>
            </OrderRow>
          ))}
        </OrderTable>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
